import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Slider,
} from '@mui/material';
import { PhotoCamera, Delete, Close, ZoomIn } from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import AvatarWithBadge from '../common/AvatarWithBadge';
import { useUploadAvatar, useDeleteAvatar } from '../../hooks/useAvatarUpload';
import { useAuth } from '../../contexts/AuthContext';
import {
  compressImage,
  validateImageFile,
  createImagePreview,
  revokeImagePreview,
  getCroppedImage,
} from '../../utils/imageCompression';
import { SubscriptionTier, UserRole } from '../common/TierBadge';

interface AvatarUploadProps {
  name: string;
  currentAvatarUrl?: string;
  avatarColor?: string;
  tier?: SubscriptionTier;
  userRole?: UserRole;
  onSuccess?: () => void;
}

export default function AvatarUpload({
  name,
  currentAvatarUrl,
  avatarColor,
  tier = 'free',
  userRole,
  onSuccess,
}: AvatarUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { refreshUser } = useAuth();
  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);

  // Crop state
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // Handle file selection
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset state
    setError(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error!);
      return;
    }

    // Cleanup old preview
    if (previewUrl) {
      revokeImagePreview(previewUrl);
    }

    // Create preview directly (we'll compress after cropping)
    const preview = createImagePreview(file);
    setPreviewUrl(preview);
    setCropDialogOpen(true);

    // Reset input
    event.target.value = '';
  }, [previewUrl]);

  // Handle crop complete
  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle upload confirmation
  const handleUpload = async () => {
    if (!previewUrl || !croppedAreaPixels) return;

    try {
      setIsProcessing(true);

      // Get cropped image
      const croppedFile = await getCroppedImage(previewUrl, croppedAreaPixels);

      // Compress the cropped image
      const compressedFile = await compressImage(croppedFile);

      // Upload
      await uploadAvatar.mutateAsync(compressedFile);

      // Refresh user data to update avatar across the app
      await refreshUser();

      // Cleanup
      if (previewUrl) {
        revokeImagePreview(previewUrl);
      }
      setPreviewUrl(null);
      setCropDialogOpen(false);

      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setError(error.response?.data?.error?.message || error.message || 'Failed to upload avatar');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (previewUrl) {
      revokeImagePreview(previewUrl);
    }
    setPreviewUrl(null);
    setCropDialogOpen(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Handle delete avatar
  const handleDelete = async () => {
    try {
      await deleteAvatar.mutateAsync();
      // Refresh user data to update avatar across the app
      await refreshUser();
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to remove avatar');
    }
  };

  const isLoading = isProcessing || uploadAvatar.isPending || deleteAvatar.isPending;

  return (
    <Box>
      {/* Current Avatar Display */}
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Box position="relative">
          <AvatarWithBadge
            name={name}
            avatarUrl={currentAvatarUrl}
            avatarColor={avatarColor}
            tier={tier}
            userRole={userRole}
            size={120}
            badgeSize={36}
          />

          {isLoading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="rgba(255,255,255,0.7)"
              borderRadius="50%"
            >
              <CircularProgress size={40} />
            </Box>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {name}
        </Typography>

        {/* Action Buttons */}
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<PhotoCamera />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            size="small"
          >
            {currentAvatarUrl ? 'Change Photo' : 'Upload Photo'}
          </Button>

          {currentAvatarUrl && (
            <IconButton
              onClick={handleDelete}
              disabled={isLoading}
              color="error"
              size="small"
              title="Remove photo"
            >
              <Delete />
            </IconButton>
          )}
        </Box>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Crop Dialog */}
      <Dialog open={cropDialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Profile Photo
          <IconButton
            onClick={handleCancel}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={isProcessing}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            {/* Cropper Area */}
            {previewUrl && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 300,
                  backgroundColor: '#333',
                  borderRadius: 2,
                  '& .reactEasyCrop_Container': {
                    borderRadius: 2,
                  },
                  '& .reactEasyCrop_Image': {
                    cursor: 'grab',
                  },
                  '& .reactEasyCrop_Image:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                <Cropper
                  image={previewUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  objectFit="contain"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </Box>
            )}

            {/* Zoom Slider */}
            <Box display="flex" alignItems="center" gap={2} px={2}>
              <ZoomIn sx={{ color: 'text.secondary' }} />
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(_, value) => setZoom(value as number)}
                disabled={isProcessing}
                sx={{ flex: 1 }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" textAlign="center">
              Drag to reposition, use slider to zoom
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isProcessing || !croppedAreaPixels}
          >
            {isProcessing ? 'Saving...' : 'Save Photo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
